import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type {
  AdminPrototypeBatch,
  AdminPrototypeShare,
  PrototypeBatch,
  PrototypeConfirmationRecord,
  PrototypeShareCreate,
  PrototypeSharePatch,
} from "@/api/types";

export type { AdminPrototypeBatch, AdminPrototypeShare, PrototypeBatch, PrototypeShare } from "@/api/types";

/** Client view grouped by batch (highest number first); only non-archived versions, newest first. */
export function usePrototypeBatches(orgSlug: string, projectSlug: string) {
  return useQuery<PrototypeBatch[], ApiError>({
    queryKey: queryKeys.prototypeBatches(orgSlug, projectSlug),
    queryFn: async () =>
      unwrap(
        await api.GET("/orgs/{slug}/projects/{pslug}/prototype-batches", {
          params: { path: { slug: orgSlug, pslug: projectSlug } },
        }),
      ),
  });
}

/** An owner confirms a batch's current version (403 for members and staff, 409 when already confirmed). */
export function useConfirmBatch(orgSlug: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation<PrototypeBatch, ApiError, { batchId: string; note: string | null }>({
    mutationFn: async ({ batchId, note }) =>
      unwrap(
        await api.POST("/orgs/{slug}/projects/{pslug}/prototype-batches/{batch_id}/confirm", {
          params: { path: { slug: orgSlug, pslug: projectSlug, batch_id: batchId } },
          body: { note },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.prototypeBatches(orgSlug, projectSlug) });
      void qc.invalidateQueries({ queryKey: queryKeys.activity(orgSlug, projectSlug) });
    },
  });
}

/** Staff view: every share including archived ones, with who was emailed. */
export function useAdminPrototypeShares(projectId: string) {
  return useQuery<AdminPrototypeShare[], ApiError>({
    queryKey: queryKeys.admin.prototypeShares(projectId),
    enabled: Boolean(projectId),
    queryFn: async () =>
      unwrap(await api.GET("/admin/projects/{project_id}/prototype-shares", { params: { path: { project_id: projectId } } })),
  });
}

/** Staff view grouped by batch (highest number first), every version incl. archived, newest first. */
export function useAdminPrototypeBatches(projectId: string) {
  return useQuery<AdminPrototypeBatch[], ApiError>({
    queryKey: queryKeys.admin.prototypeBatches(projectId),
    enabled: Boolean(projectId),
    queryFn: async () =>
      unwrap(await api.GET("/admin/projects/{project_id}/prototype-batches", { params: { path: { project_id: projectId } } })),
  });
}

function useInvalidateShares(projectId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.admin.prototypeShares(projectId) });
    void qc.invalidateQueries({ queryKey: queryKeys.admin.prototypeBatches(projectId) });
    void qc.invalidateQueries({ queryKey: ["orgs"] });
  };
}

export function useCreatePrototypeShare(projectId: string) {
  const invalidate = useInvalidateShares(projectId);
  return useMutation<AdminPrototypeShare, ApiError, PrototypeShareCreate>({
    mutationFn: async (body) =>
      unwrap(await api.POST("/admin/projects/{project_id}/prototype-shares", { params: { path: { project_id: projectId } }, body })),
    onSuccess: invalidate,
  });
}

export function usePatchPrototypeShare(projectId: string) {
  const invalidate = useInvalidateShares(projectId);
  return useMutation<AdminPrototypeShare, ApiError, { id: string; body: PrototypeSharePatch }>({
    mutationFn: async ({ id, body }) =>
      unwrap(await api.PATCH("/admin/prototype-shares/{share_id}", { params: { path: { share_id: id } }, body })),
    onSuccess: invalidate,
  });
}

export function useSendPrototypeShare(projectId: string) {
  const invalidate = useInvalidateShares(projectId);
  return useMutation<AdminPrototypeShare, ApiError, { id: string; recipientIds: string[] }>({
    mutationFn: async ({ id, recipientIds }) =>
      unwrap(
        await api.POST("/admin/prototype-shares/{share_id}/send", {
          params: { path: { share_id: id } },
          body: { recipient_user_ids: recipientIds },
        }),
      ),
    onSuccess: invalidate,
  });
}

/** Staff record that an owner confirmed a version by phone, WhatsApp, in person or email. */
export function useRecordConfirmation(projectId: string) {
  const invalidate = useInvalidateShares(projectId);
  return useMutation<AdminPrototypeShare, ApiError, { shareId: string; body: PrototypeConfirmationRecord }>({
    mutationFn: async ({ shareId, body }) =>
      unwrap(await api.POST("/admin/prototype-shares/{share_id}/confirm", { params: { path: { share_id: shareId } }, body })),
    onSuccess: invalidate,
  });
}
